import React, { Component } from 'react'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import { sortBy, map, startCase, isUndefined, isNull, groupBy, orderBy, omit } from 'lodash'

// PD-30615 MR3: a plain flex "table" -- one flex-1 column per rating factor,
// column count varies by product line, so a fixed grid-template can't be
// declared up front. No dedicated stylesheet rules for any of these classes
// (age-band/child-rate/accidental-death/ci-rates are query hooks only), so
// this stays inside the panel's .ideon-ui-scope with no legacy markup.
const ROW_CLASS = 'flex gap-4 text-sm py-1 border-b border-ideon-gray-40 dark:border-ideon-gray-80'
const HEADER_ROW_CLASS = 'flex gap-4 text-sm font-semibold mt-4 mb-1'
const CELL_CLASS = 'flex-1'

export class PlanRateComponent extends Component {
  static propTypes = {
    plan: PropTypes.object.isRequired,
    quoteRates: PropTypes.array
  }

  get quoteRate() {
    return this.props.quoteRates.find((rate) => rate.plan_id === this.props.plan.id)
  }

  get premiumBands() {
    const productLine = this.props.plan.product_line

    return this.quoteRate.premiums[productLine].premium_bands
  }

  get ratings() {
    const ratings = this.premiumBands.map((rating) => Object.assign(this.ratingOrder, rating))

    if (this.props.plan.product_line === 'critical_illness') {
      return map(groupBy(ratings, 'benefit_amount'), (values) => orderBy(values, 'minimum_age', ['asc']))
    }

    return sortBy(ratings, ['minimum_age'], ['asc'])
  }

  get childRate() {
    return this.quoteRate.premiums.life.child_rate
  }

  get accidentalDeathDismemberment() {
    return this.quoteRate.premiums.accidental_death_dismemberment.employee
  }

  get isLife() {
    return this.props.plan.product_line === 'life'
  }

  get lifeRatingOrder() {
    return {
      minimum_age: null,
      maximum_age: null,
      employee: null,
      employee_tobacco: null,
      spouse: null
    }
  }

  get disabilityRatingOrder() {
    return {
      minimum_age: null,
      maximum_age: null,
      employee: null
    }
  }

  get hospitalIndemnityRatingOrder() {
    return {
      minimum_age: null,
      maximum_age: null,
      employee_only: null,
      employee_plus_children: null,
      employee_plus_spouse: null,
      employee_plus_family: null
    }
  }

  get criticalIllnessRatingOrder() {
    return {
      minimum_age: null,
      maximum_age: null,
      employee_only: null,
      employee_plus_children: null,
      employee_plus_spouse: null,
      employee_plus_family: null
    }
  }

  get ratingOrder() {
    switch (this.props.plan.product_line) {
    case 'life':
      return this.lifeRatingOrder
    case 'disability':
      return this.disabilityRatingOrder
    case 'hospital_indemnity':
      return this.hospitalIndemnityRatingOrder
    case 'critical_illness':
      return this.criticalIllnessRatingOrder
    default:
      return null
    }
  }

  get ratingHeader() {
    switch (this.props.plan.product_line) {
    case 'life':
      return 'Rates are monthly per $1,000 of benefit coverage'
    case 'disability':
      return 'STD rates are monthly per $10 of benefit coverage, LTD rates are monthly per $100 of benefit coverage'
    default:
      return null
    }
  }

  ratingRows = (rating) => {
    return map(rating, (value, key) => {
      return (
        <div className={CELL_CLASS} key={`rating-${rating.id}-${key}`}>
          <span>{value}</span>
        </div>
      )
    })
  }

  criticalIllnessSection = (rows, type, amount) => {
    return (
      <div key={`ci-${type}`} className="ci-rates">
        <div className={HEADER_ROW_CLASS}>
          {startCase(type).replace(' ', '-')} Premium Bands
        </div>

        <div className="text-sm italic mt-1">
          Benefit Amount: {amount} | {startCase(type).replace(' ', '-')}
        </div>

        <div className={HEADER_ROW_CLASS}>
          <div className={CELL_CLASS}>Age</div>
          <div className={CELL_CLASS}>Employee Only</div>
          <div className={CELL_CLASS}>Employee Plus Children</div>
          <div className={CELL_CLASS}>Employee Plus Spouse</div>
          <div className={CELL_CLASS}>Employee Plus Family</div>
        </div>

        {rows}
      </div>
    )
  }

  renderCriticalIllnessRates = () => {
    const sections = []

    map(this.ratings, (amount) => {
      const tobaccoRows = []
      const nonTobaccoRows = []

      map(amount, (ageBand, index) => {
        const tobaccoRates = this.addAgeRanges(ageBand.tobacco, ageBand.minimum_age, ageBand.maximum_age)
        const nonTobaccoRates = this.addAgeRanges(ageBand.non_tobacco, ageBand.minimum_age, ageBand.maximum_age)
        const tobaccoRating = Object.assign(this.ratingOrder, tobaccoRates)
        const nonTobaccoRating = Object.assign(this.ratingOrder, nonTobaccoRates)

        tobaccoRows.push(
          <div key={`rate_tobacco_row_${index}`} className={ROW_CLASS}>
            <div className={CELL_CLASS}>
              <span>{this.ageString(tobaccoRating.minimum_age, tobaccoRating.maximum_age)}</span>
            </div>

            {this.ratingRows(omit(tobaccoRating, ['minimum_age', 'maximum_age']))}
          </div>
        )

        nonTobaccoRows.push(
          <div key={`rate_tobacco_row_${index}`} className={ROW_CLASS}>
            <div className={CELL_CLASS}>
              <span>{this.ageString(nonTobaccoRating.minimum_age, nonTobaccoRating.maximum_age)}</span>
            </div>

            {this.ratingRows(omit(nonTobaccoRating, ['minimum_age', 'maximum_age']))}
          </div>
        )
      })

      sections.push(this.criticalIllnessSection(tobaccoRows, 'tobacco', amount[0].benefit_amount))
      sections.push(this.criticalIllnessSection(nonTobaccoRows, 'non-tobacco', amount[0].benefit_amount))
    })

    return sections
  }

  addAgeRanges(rates, minAge, maxAge) {
    const ratesWithAges = rates
    ratesWithAges.minimum_age = minAge
    ratesWithAges.maximum_age = maxAge
    return ratesWithAges
  }

  ageString(min, max) {
    const minAge = isNull(max) ? `${min} +` : min
    const maxAge = !isNull(max) ? max : ''
    const separator = !isNull(max) ? '-' : ''

    return `${minAge} ${separator} ${maxAge}`
  }

  renderRatings = () => {
    if (this.props.plan.product_line === 'critical_illness') {
      return (this.renderCriticalIllnessRates())
    }

    return (
      <>
        {!isNull(this.ratingHeader) && (
          <div className="text-sm italic mt-1">{this.ratingHeader}</div>
        )}

        <div className={HEADER_ROW_CLASS}>Premium Bands</div>

        <div className={HEADER_ROW_CLASS}>
          <div className={CELL_CLASS}>Age</div>

          {Object.keys(omit(this.ratings[0], ['minimum_age', 'maximum_age'])).map((key, index) => (
            <div key={`rating_${key}_${index}`} className={CELL_CLASS}>{startCase(key)}</div>
          ))}
        </div>

        {this.renderAgeBands()}
      </>
    )
  }

  renderAgeBands = () => {
    return this.ratings.map((rating, index) => (
      <div key={`age-banded-${rating.id}-${index}`} className={`${ROW_CLASS} age-band`}>
        <div className={CELL_CLASS}>
          <span>{this.ageString(rating.minimum_age, rating.maximum_age)}</span>
        </div>

        {Object.keys(omit(rating, ['minimum_age', 'maximum_age'])).map((key, i) => (
          <div key={`values-${rating.id}-${key}-${i}`} className={CELL_CLASS}>
            <span>{this.ratings[index][key]}</span>
          </div>
        ))}
      </div>
    ))
  }

  renderChildRates = () => {
    return (
      <>
        <div className={HEADER_ROW_CLASS}>Child Rate</div>

        <div className={`${ROW_CLASS} child-rate`}>
          <div className={CELL_CLASS}><span>Child rate</span></div>
          <div className={CELL_CLASS}><span>{this.childRate}</span></div>
        </div>
      </>
    )
  }

  renderAccidentalDeathDismemberments = () => {
    return (
      <>
        <div className={HEADER_ROW_CLASS}>Accidental Death and Dismemberment Rates</div>

        <div className={`${ROW_CLASS} accidental-death`}>
          <div className={CELL_CLASS}><span>Accidental Death Dismemberment</span></div>
          <div className={CELL_CLASS}>{this.accidentalDeathDismemberment}</div>
        </div>
      </>
    )
  }

  render() {
    const { plan } = this.props
    if (isUndefined(this.quoteRate)) { return null }

    return (
      <div key={`${plan.id}-rates`} id={`${plan.id}-rates`}>
        {this.renderRatings()}
        {this.isLife && this.renderChildRates()}
        {this.isLife && this.renderAccidentalDeathDismemberments()}
      </div>
    )
  }
}

const mapStateToProps = (state) => {
  return {
    quoteRates: state.quoteRates.create.responseAttributes
  }
}

export default connect(mapStateToProps, null)(PlanRateComponent)
